import { useEffect, useRef } from "react";

// dataLayer is already declared in src/lib/analytics.ts
// This file uses the existing declaration

// Global flag to prevent duplicate initialization across component remounts
let gtmInitialized = false;
const gtmScripts = new Set<string>();

/**
 * Google Tag Manager component
 * Initializes non-page-view GTM tags; analytics.ts owns GA4 page views.
 */
export function GoogleTagManager() {
  const gtmId = import.meta.env.VITE_GTM_ID || "GTM-TKFD8DPF";
  const initializedRef = useRef(false);

  useEffect(() => {
    // Prevent duplicate initialization in StrictMode
    if (initializedRef.current || gtmInitialized) {
      return;
    }

    console.log("[GTM] Initializing Google Tag Manager with ID:", gtmId);
    console.log("[GTM] VITE_GTM_ID env var:", import.meta.env.VITE_GTM_ID);

    // Check if GTM script already exists
    const existingScript = document.querySelector(
      `script[src*="googletagmanager.com/gtm.js"]`,
    );
    if (existingScript) {
      console.log("[GTM] Google Tag Manager script already loaded");
      gtmInitialized = true;
      initializedRef.current = true;
      return;
    }

    // Check if we've already tried to load this GTM ID
    if (gtmScripts.has(gtmId)) {
      console.log("[GTM] Already attempted to load GTM with this ID");
      return;
    }

    // Mark as attempting to load
    gtmScripts.add(gtmId);
    initializedRef.current = true;
    gtmInitialized = true;

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];

    // Load GTM script
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
    script.onerror = () => {
      console.error("[GTM] Failed to load Google Tag Manager script");
      // Reset flags on error so it can retry if needed
      gtmScripts.delete(gtmId);
      gtmInitialized = false;
      initializedRef.current = false;
    };
    script.onload = () => {
      console.log("[GTM] Google Tag Manager script loaded successfully");
    };
    document.head.appendChild(script);

    return () => {
      // Don't reset on unmount in StrictMode - keep the script loaded
    };
  }, [gtmId]);

  return (
    <>
      {/* Google Tag Manager (noscript) */}
      <noscript>
        <iframe
          src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
          height="0"
          width="0"
          style={{ display: "none", visibility: "hidden" }}
        />
      </noscript>
    </>
  );
}
